/**
 * Pure decision helpers extracted from ZonesDevices.tsx's handleLinkPerson flow
 * so they're unit-testable without mounting the (11k+ line) component.
 */

/**
 * Resolve a device person's `employeeNo` to the device's internal integer
 * user id, when it actually looks like one.
 *
 * `employeeNo` is only a plain integer for some devices; for others (e.g.
 * ControlID face-linked persons) it's a non-numeric hex registration string.
 * A bare `parseInt()` would silently truncate a hex string like
 * "6a8ef19a34e9c8fa441c7fc2" to a bogus integer (6) instead of failing, which
 * previously caused fingerprint import to look up the wrong device user and
 * report "0 biometrics found" with no error. Requiring the whole string to be
 * digits before parsing is what fixes that.
 */
export function resolveForcedUserID(employeeNo: string | undefined | null): number | undefined {
  if (!employeeNo || !/^\d+$/.test(employeeNo)) {
    return undefined;
  }
  return parseInt(employeeNo, 10);
}

export interface BiometryPullDevice {
  manufacturer?: string | null;
  device_type?: string | null;
}

/**
 * Whether a device is a "digital" (fingerprint-capable) ControlID unit that
 * fingerprint import should run against — i.e. NOT one of the facial-only
 * device types, even though those are also manufactured by ControlID.
 */
export function shouldPullDeviceBiometry(device: BiometryPullDevice): boolean {
  return (
    !!device.manufacturer?.toLowerCase().includes('control') &&
    device.device_type !== 'controlid_face' &&
    device.device_type !== 'facial_reader' &&
    device.device_type !== 'facial_recognition'
  );
}
